import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';
import ARFData, { AppointmentTimeslots, ASNListLine, EquipmentTypes, RequesterTypes } from '../viewModels/ARFData';
import ARF from '../services/ARF';
import './ARFForm.scss'
import ARFDetail from './ARFDetail'
import CanadaPhoneInput from '../phoneInput/PhoneInput';
import ARFModalContext from './context'
import GenericModal from '../modal/Modal';
import ARFModal from './ARFModal'
import ARFConfirmation from './ARFConfirmation';
import PleaseWaitOverlay from '../loading/PleaseWaitOverlay';
import CallToken from '../settings/CallToken';
import IntegerInput from '../input/IntegerInput'

const type = RequesterTypes.Agency;

const ARFForm = () => {
  const [currForm, setCurrForm] = useState({
    submitDate: new Date(),
    requesterType : type,
    producerName: '',
    carrierName: '',
    containerNum: '',
    equipmentType: '',

    reqName: '',
    reqPhone: '',
    reqEmailAddress: '',

    detailLines: [{asnNo: '', cases: 0}],

    totalPallets: 0,
    totalCases: 0,
    prefApptDate: new Date(),
    prefApptTime: '',
    comments: ''
  } as ARFData);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [errorMsg, setErrorMsg] = useState([] as string[]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    validate();
  }, [currForm]);

  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }
  const options = [<option value='' key='-1'>Please specify</option>];
  EquipmentTypes.forEach((val: string, index: number) => { options.push(<option value={val} key={index}>{val}</option>); });

  const deliveryTimes = [<option value='' key='-1'>Please select a time</option>];
  AppointmentTimeslots.forEach( (val: string, index: number) => { deliveryTimes.push( <option value={val} key={index}>{val}</option>); });

  const handleDateChange = (date: Date | null, event: React.SyntheticEvent<any, Event> | undefined) => {
    setCurrForm((currForm) => ({
      ...currForm,
      prefApptDate: date
    }));
  }

  const handlePhoneChange = (newVal: string) => {
    const newForm = {
      ...currForm
    };

    newForm.reqPhone = newVal;

    setCurrForm(newForm);
  }


  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const newForm = {
      ...currForm
    };

    if (event.target.name === "requesterType") {
      newForm[event.target.name] = parseInt(event.target.value) as RequesterTypes;
    } else if (event.target.name === "totalPallets") {
      if (!event.target.value || !event.target.value.trim()) {
        newForm[event.target.name] = 0;
      } else if (!isNaN(parseInt(event.target.value))) {
        newForm[event.target.name] = parseInt(event.target.value.replace(/\.$/, ''));
      }
    } else {
      (newForm as any)[event.target.name] = event.target.value;
    }


    setCurrForm(newForm);
  }

  const handleSubmit = async () : Promise<boolean> => {
    setSubmitting(true);
    setSubmitSuccess(false);

    return await ARF.submit(currForm).then((result: any) => {

      if (result) {
        setSubmitSuccess(result.content);

        if (result.content && !result.is_error) {
          setErrorMsg(['']);
        } else {
          setErrorMsg(['Could not submit - please refresh and try again. If it continues to fail, please contact Connect Logistics at CannabisReceiving@Connect-Logistics.com']);
        }

        return result.content;
      } else {
        setErrorMsg(['Could not submit - please refresh and try again. If it continues to fail, please contact Connect Logistics at CannabisReceiving@Connect-Logistics.com']);

        return false;
      }
    }).finally(() => {
      setSubmitting(false);
    });
  }

  const handleLineChanged = (asnNo: string|null, cases: number|null, lineNo: number) => {
    const newForm = {
      ...currForm
    };

    if (cases !== null) { //accept 0
      newForm.detailLines[lineNo].cases = cases;
    }

    if (asnNo !== null) { //accept ''
      newForm.detailLines[lineNo].asnNo = asnNo;
    }

    newForm.totalCases = 0;
    newForm.detailLines.forEach((item: ASNListLine) => { newForm.totalCases += item.cases });

    setCurrForm(newForm);
  }

  const isValidPhone = (): boolean => {
    let isValid = true;

    if (!currForm.reqPhone || !currForm.reqPhone.trim()) {
      isValid = false;
    } else if ((currForm.reqPhone.length < 10) || (currForm.reqPhone.length > 12)) {
      isValid = false;
    }

    return isValid;
  }

  const validate = () => {
    const errorMsg = [];
    let isValid = true;

    if (!currForm.producerName || !currForm.producerName.trim()) {
      errorMsg.push('Please specify producer\'s name');
      isValid = false;
    }

    if (!currForm.reqName || !currForm.reqName.trim()) {
      errorMsg.push('Please enter requester\'s name');
      isValid = false;
    }

    if (!isValidPhone()) {
      if (!currForm.reqPhone || !currForm.reqPhone.trim()) {
        errorMsg.push('Please enter Requester\'s phone number');
      } else if ((currForm.reqPhone.length < 10) || (currForm.reqPhone.length > 12)) {
        errorMsg.push('Please enter a valid 10 digit number');
      }

      isValid = false;
    }

    if (!currForm.reqEmailAddress || !currForm.reqEmailAddress.trim()) {
      errorMsg.push('Please provide the requester\'s email');
      isValid = false;
    }

    if (!currForm.prefApptDate && (currForm.prefApptDate ?? (new Date()) > new Date())) {
      errorMsg.push('Preferred appointment date must be in the future');
      isValid = false;
    }

    if (!currForm.prefApptTime) {
      errorMsg.push('Please specify your preferred appointment time');
      isValid = false;
    }

    if (!currForm.detailLines || currForm.detailLines.length == 0) {
      errorMsg.push('At least one ASN must be specified');
      isValid = false;
    } else {
      let hasRecord = false;

      for (let i = 0; i < currForm.detailLines.length; i++) {
        if (currForm.detailLines[i].asnNo && currForm.detailLines[i].asnNo.trim()) {
          if (!(currForm.detailLines[i].cases && (currForm.detailLines[i].cases > 0))) {
            //but no cases
            errorMsg.push('if an asn number is entered, case count should be greater than zero');
            isValid = false;
          } else {
            hasRecord = true;
          }
        } else if (currForm.detailLines[i].cases > 0) {
          //no asn but cases
          errorMsg.push('if a quantity of cases is entered, asn number cannot be blank');
          isValid = false;
        }
      }

      if (!hasRecord) {
        errorMsg.push('At least one ASN must be specified');
        isValid = false;
      }
    }

    setErrorMsg(errorMsg);
    setIsValid(isValid);
  }

  const addLine = () => {
    const newForm = {
      ...currForm
    };

    newForm.detailLines.push({ cases: 0, asnNo: '' });

    setCurrForm(newForm);
  }

  const removeLine = (index: number) => {
    const newForm = {
      ...currForm
    };

    newForm.detailLines.splice(index, 1);

    setCurrForm(newForm);
  }

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  const asnDetailItems: React.JSX.Element[] = [];
  currForm.detailLines.forEach((item: ASNListLine, index: number) => {
    asnDetailItems.push(<ARFDetail cases={item.cases} asnNo={item.asnNo} index={index} key={index} handleChange={handleLineChanged} addLine={addLine} removeLine={removeLine} />)
  });

  return submitSuccess ? <ARFConfirmation></ARFConfirmation> : <><hr></hr><div className='arfContainer'>
    <PleaseWaitOverlay show={submitting} message={ "Submitting... please wait" } />
    <header>
      <h1>AGLC - Appointment Request Form</h1>
      <div>Date of Request: {currForm.submitDate.toDateString()}</div>  
    </header>
    <form className="arf">
      <CallToken></CallToken>
      <hr></hr>
      <h2>Requestor:</h2>
      <div>
        <label>Select One*:</label>
        <label>
          <input type="radio" name="requesterType" value={RequesterTypes.Agency} checked={currForm.requesterType == RequesterTypes.Agency} onChange={handleFieldChange}></input>
          Agency Request
        </label>
        <label>
          <input type="radio" name="requesterType" value={RequesterTypes.FreightForwarder} checked={currForm.requesterType == RequesterTypes.FreightForwarder} onChange={handleFieldChange}></input>
          Freight Forwarder Request
        </label>
      </div>
      <div className="requestorDetails">
        <div className="deliveryInfo">
          <h2>Delivery Information</h2>
          <label>Licensed Producer Name*:</label>
          <input type="text" name="producerName" className={currForm.producerName ? '': 'error' } onChange={handleFieldChange} value={currForm.producerName} />
          <label>Carrier Name:</label>
          <input type="text" name="carrierName" onChange={handleFieldChange} value={currForm.carrierName} />
          <label>Container or Trailer #:</label>
          <input type="text" name="containerNum" onChange={handleFieldChange} value={currForm.containerNum} />
          <label>Equipment Type:</label>
          <select name="equipmentType" value={currForm.equipmentType} onChange={handleFieldChange}>
            {options}
          </select>
        </div>
        <div className="contactInfo">
          <h2>Your Contact Information</h2>
          <label>Name*:</label>
          <input type="text" name="reqName" className={currForm.reqName ? '': 'error' } onChange={handleFieldChange} value={currForm.reqName} />
          <label>Phone*:</label>
          <CanadaPhoneInput value={currForm.reqPhone} inputClass={isValidPhone() ? '' : 'error'} onChange={handlePhoneChange} />
          <label>Email*:</label>
          <input type="email" name="reqEmailAddress" className={currForm.reqEmailAddress ? '': 'error' } onChange={handleFieldChange} value={currForm.reqEmailAddress} />
        </div>
      </div>
      <div className="asnDetails">
        <h2>ASN Detail</h2>
        <div className="asnLines">
          <div>
            <label>ASN#*</label>
          </div>
          <div className="fieldWithOptions">
            <label>Cases*</label>
          </div>
          {asnDetailItems}
        </div>
        <div className="asnInfo">
          <label>Total Count of Pallets:</label>
          <IntegerInput type="number" name="totalPallets" onChange={handleFieldChange} value={currForm.totalPallets?.toString()} step={1} />
          <label>Preferred Appt Date:</label>
          <DatePicker
              className={classNames({
                'form-control': true,
                error: !currForm.prefApptDate,
              })}
              required={true}
            name="prefApptDate"
            selected={currForm.prefApptDate}
            onChange={handleDateChange}
            dateFormat="MMMM dd, yyyy"
            disabled={false}
            minDate={new Date()}
            maxDate={maxDate}
            autoComplete={'off'} />
        </div>
        <div className="asnInfo">
          <label>Total Count of Cases*:</label>
          <input type="text" name="totalCases" disabled value={currForm.totalCases} className={currForm.totalCases ? '': 'error' } />
          <label>Preferred Appt Time:</label>
          <select name="prefApptTime" className={currForm.prefApptTime ? '' : 'error'} value={currForm.prefApptTime} onChange={handleFieldChange}>
            {deliveryTimes}
          </select>
        </div>
      </div>
      <div className="comments">
        <label>Comments*:</label>
        <textarea name="comments" onChange={handleFieldChange} value={currForm.comments} />
      </div>
      <div className="errors">
        {errorMsg.map((val: string, index: number) => <div key={index}>{val}</div>) }
      </div>
      <div className="arfActions">
        <ARFModalContext.Provider
          value={{
            isOpen: isModalOpen,
            open: openModal,
            close: closeModal,
            submit: handleSubmit
          }}
        >
        <ARFModalContext.Consumer>
          {({ open }) => {
            const onClick = () => {
              open()
            }

              return <button className={'btn btn-orange'} onClick={onClick} disabled={!isValid} type="button">Submit ARF</button>;
          }}
          </ARFModalContext.Consumer>
          <ARFModalContext.Consumer>
            {({ isOpen, close, submit }) => {
              return <GenericModal isOpen={isOpen}>
                        <ARFModal
                  email={currForm.reqEmailAddress}
                  close={close}
                  submit={submit}
                        />;
                      </GenericModal>;
            }}
            </ARFModalContext.Consumer>
        </ARFModalContext.Provider>
      </div>
    </form>
  </div></>;
};

export default ARFForm;
